import React from 'react';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';

export type StatusTone = 'warning' | 'success' | 'error' | 'info' | 'muted';

/** Outlined chips: the tone on the border and text, no fill (for an outline `Badge` that wraps). */
export const TONE_STYLE: Record<StatusTone, string> = {
  warning: 'border-warning text-warning',
  success: 'border-success text-success',
  error: 'border-error text-error',
  info: 'border-primary text-primary',
  muted: 'text-muted-foreground',
};

/** The usual tone of a request status (pending, approved, rejected, withdrawn, ...). */
const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  requested: 'warning',
  inprogress: 'info',
  approved: 'success',
  completed: 'success',
  submitted: 'success',
  rejected: 'error',
  declined: 'error',
  withdrawn: 'muted',
  cancelled: 'muted',
  canceled: 'muted',
  archived: 'muted',
};

export const statusTone = (status: string | undefined): StatusTone =>
  STATUS_TONE[(status ?? '').replace(/[\s_-]/g, '').toLowerCase()] ?? 'muted';

interface StatusChipProps {
  /** The tone; by default the usual one of `status` (see `statusTone`). */
  tone?: StatusTone;
  status?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * A status as an outlined chip, as on the attendance pages. Long text is cut to the chip's width
 * (the app-wide `OverflowTooltip` shows it in full).
 */
export const StatusChip = ({ tone, status, children, className }: Readonly<StatusChipProps>) => (
  <Badge
    variant="outline"
    className={cn('max-w-full font-medium', TONE_STYLE[tone ?? statusTone(status)], className)}
  >
    <span className="truncate">{children}</span>
  </Badge>
);
